import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocalVideoTrack, createLocalVideoTrack } from 'livekit-client';
import debounce from 'lodash/debounce';
import { VIDEO_PRESETS } from 'consts';
import { useUserSettingsContext } from 'contexts';
import { virtualBackgroundService as service } from 'features/VirtualBackground/classes/VirtualBackgroundService';

export const useTrackForVirtualBackground = () => {
    const { videoDeviceId } = useUserSettingsContext();

    const [localVideoTrack, setLocalVideoTrack] = useState<LocalVideoTrack | null>(null);
    const localVideoTrackRef = useRef<LocalVideoTrack | null>(null);
    const isMounted = useRef(true);
    const isCreatingTrack = useRef(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const getAbortController = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        return abortControllerRef.current;
    }, []);

    const createTempTrack = useCallback(async () => {
        if (isCreatingTrack.current || !isMounted.current) {
            return;
        }

        isCreatingTrack.current = true;
        const controller = getAbortController();

        try {
            // Останавливаем текущий трек
            const currentTrack = localVideoTrackRef.current;

            if (currentTrack) {
                try {
                    currentTrack.stop();
                    await service.removeManager('modal');
                } catch (error) {
                    console.warn('Error stopping track:', error);
                }
            }

            // Проверяем отмену перед созданием трека
            if (controller.signal.aborted) {
                return;
            }

            // Создаём новый трек
            const newTrack = await createLocalVideoTrack({
                deviceId: videoDeviceId,
                // Для превью в маленьком окне не обязательно большое разрешение
                resolution: VIDEO_PRESETS.LOW.resolution,
            });

            // Финальная проверка отмены
            if (controller.signal.aborted || !isMounted.current) {
                newTrack.stop();
                return;
            }

            // Применяем процессор к треку через сервис
            await service.applyToTrack('modal', newTrack);

            setLocalVideoTrack(newTrack);
            localVideoTrackRef.current = newTrack;
        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error('Failed to create track:', error);
            }
        } finally {
            if (isMounted.current) {
                isCreatingTrack.current = false;
            }
        }
    }, [videoDeviceId, getAbortController]);

    const debouncedCreateTempTrack = useMemo(() => debounce(createTempTrack, 300), [createTempTrack]);

    // Эффект для создания трека при изменении устройства
    useEffect(() => {
        debouncedCreateTempTrack();

        return () => {
            debouncedCreateTempTrack.cancel();
        };
    }, [videoDeviceId, debouncedCreateTempTrack]);

    // Эффект для очистки при размонтировании
    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;

            // Отменяем все операции
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }

            service.removeManager('modal');

            const localTrack = localVideoTrackRef.current;
            if (localTrack) {
                try {
                    localTrack.stopProcessor();
                    localTrack.stop();
                } catch (error) {
                    console.warn(error);
                }
            }
        };
    }, []);

    return useMemo(
        () => ({
            localVideoTrack,
            recreateTrack: createTempTrack,
            hasTrack: !!localVideoTrack,
        }),
        [localVideoTrack, createTempTrack],
    );
};
